import {
    CheckCircledIcon, UpdateIcon
} from '@radix-ui/react-icons';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import '../App.css';
import { DatePickerSingle } from '../components/datePickerSingle.jsx';
import { Button } from '../components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import SelectBunnyPopover from '../components/selectBunnyPopver';
import api from '../lib/api';

function LitterInformation() {

    const navigate = useNavigate();
    const form = useForm();
    let { litterId } = useParams();

    litterId = parseInt(litterId);

    const [litter, setLitter] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        father: null,
        mother: null,
        no_children: null,
        birth_date: null,
    });

    const fetchLitterInformation = useCallback(() => {
        setIsLoading(true); // Start loading data

        api.get(`https://skhrf-api.cmdigital.se/v1/litters?litterId=${litterId}`)
            .then(response => {
                const litterData = response.data;
                setLitter(litterData);
                setFormData({
                    name: litterData?.name || '',
                    father: litterData?.father.id || null,
                    mother: litterData?.mother.id || null,
                    no_children: litterData?.no_children || null,
                    birth_date: litterData?.birth_date.toLocaleString('sv').split('T')[0] || null,
                });
                setIsLoading(false); // Data fetched, set loading to false
            })
            .catch(error => {
                console.error('Error fetching litter information:', error);
                setIsLoading(false); // Error occurred, set loading to false
            });
    }, [litterId]);

    const sendLitterInformation = (e) => {
        if (isLoading) return;
        e.preventDefault();
        setIsLoading(true);

        api.put(`https://skhrf-api.cmdigital.se/v1/litters/edit`, {
            litterId,
            ...formData,
        }).then(response => {

            setIsLoading(false);
            fetchLitterInformation();
            toast({
                variant: 'success',
                title: 'Informationen uppdaterades!',
                description: "Informationen uppdaterades utan problem.",
            });
            navigate(`/litters/${litter?.id}/`);
        }).catch(error => {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
            setIsLoading(false);
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleValueChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleDateChange = (date, name) => {
        if (!date) {
            return; // Early return if the date is null or undefined
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: date.toLocaleString('sv').split(' ')[0], // Update the specific field (startDate, endDate, etc.)
        }));
    };

    useEffect(() => {
        fetchLitterInformation();
    }, [fetchLitterInformation]);

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl mb-2'>
                    {isLoading ? (
                        <Skeleton className='w-64 h-8' />
                    ) : (
                        `Redigera ${litter.name}`
                    )}
                </h1>
                <Form {...form} >
                    <form className="flex flex-col gap-6 w-full sm:w-1/2">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Namn</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Johans kull" name="name" value={formData.name} onChange={handleChange} disabled={isLoading} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="birth_date"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Kullens födelsedatum</FormLabel>
                                    <FormControl>
                                        <DatePickerSingle onDateChange={(date) => handleDateChange(date, field.name)} selectedDate={litter.birth_date} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <SelectBunnyPopover
                            form={form}
                            name="father"
                            label="Far"
                            gender='male'
                            onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, father: bunnyId }))}
                            selectedBunny={[{
                                "id": litter?.father?.id,
                                "full_name": litter?.father?.name,
                                "registration_number": litter?.father?.registration_number,
                            }]}
                            allowDead={true}
                        />
                        <SelectBunnyPopover
                            form={form}
                            name="mother"
                            label="Mor"
                            gender='female'
                            onSelect={(bunnyId) => setFormData(prevState => ({ ...prevState, mother: bunnyId }))}
                            selectedBunny={[{
                                "id": litter?.mother?.id,
                                "full_name": litter?.mother?.name,
                                "registration_number": litter?.mother?.registration_number,
                            }]}
                            allowDead={true}
                        />
                        <FormField
                            control={form.control}
                            name="no_children"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Antal födda kaniner</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            value={formData.no_children}
                                            onChange={handleValueChange}
                                            type='number'
                                            min='0'
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button type="submit" disabled={isLoading} onClick={sendLitterInformation}>
                            {isLoading ? <UpdateIcon className='loader-spinner' /> : <><CheckCircledIcon className='mr-3' />Uppdatera</>}
                        </Button>
                    </form>
                </Form>
            </div>
        </div>
    );
}

export default LitterInformation;
